// TeamDetails.jsx
import React, { useContext, useMemo, useState, useEffect } from "react";
import styles from "./TeamDetails.module.scss";
import { FiChevronDown, FiChevronUp } from "react-icons/fi";
import { BiMailSend } from "react-icons/bi";
import { useTranslation } from "react-i18next";

import NavContext from "../../../context/NavContext";
import { useUserInviteContext } from "../../../context/UserInviteContext";

import InputForms from "../../UiComponents/forms/InputForms";
import ResendInviteCard from "../InvitUser/ResendInviteCard";
import UserCard from "../userCard/UserCard";
import InviteForms from "../InvitUser/InviteForms";
// import Button from "../../UiComponents/Button";
import Loader from "components/UiComponents/loader/Loader";

import { useTeamData } from "../userCard/UseTeamData";

export default function TeamDetails({ defaultInviteOpen = false }) {
  const { t } = useTranslation();
  const { ownerId, userAuth, adminProfile, superAdmin, ownerProfile } = useContext(NavContext);
  const { inviteId } = useUserInviteContext();

  // ✅ remplace useModal
  const [showInvite, setShowInvite] = useState(defaultInviteOpen);
  const [invitationsOpen, setInvitationsOpen] = useState(true);
  const [teamOpen, setTeamOpen] = useState(true);

  // ✅ si le parent demande l’ouverture (clic tutorial), on suit
  useEffect(() => {
    setShowInvite(defaultInviteOpen);
  }, [defaultInviteOpen]);

  const toggleInvite = () => setShowInvite((v) => !v);

  // UI local
  const [searchUserInviteWaiting, setSearchUserInviteWaiting] = useState("");
  const [sortedInviteUser, setSortedInviteUser] = useState("");
  const [searchUser, setSearchUser] = useState("");
  const [sorted, setSorted] = useState("");

  const team = useTeamData({ ownerId, inviteId, userAuth });
  const isLoading = team.loadingUsers || team.loadingInvites;

  const allCabines = useMemo(() => {
    if (isLoading) return [];
    return team.inviteUser?.[0]?.cabines?.[0] || [];
  }, [isLoading, team.inviteUser]);

  const filteredInvites = useMemo(() => {
    if (isLoading) return [];
    if (!Array.isArray(team.inviteWaiting)) return [];
    const q = (searchUserInviteWaiting || "").toLowerCase();
    return team.inviteWaiting
      .filter((u) => {
        const dn = (u.display_name || "").toLowerCase();
        const em = (u.email || "").toLowerCase();
        return dn.includes(q) || em.includes(q);
      })
      .sort((a, b) => {
        let valA, valB;
        switch (sortedInviteUser) {
          case "ascLogin":
            valA = a.display_name || "";
            valB = b.display_name || "";
            return valA.localeCompare(valB);
          case "dscLogin":
            valA = a.display_name || "";
            valB = b.display_name || "";
            return valB.localeCompare(valA);
          case "ascName":
            valA = a.email || "";
            valB = b.email || "";
            return valA.localeCompare(valB);
          case "dscName":
            valA = a.email || "";
            valB = b.email || "";
            return valB.localeCompare(valA);
          case "ascExpiration":
            return a.when - b.when;
          case "dscExpiration":
            return b.when - a.when;
          default:
            valA = a.display_name || "";
            valB = b.display_name || "";
            return valA.localeCompare(valB);
        }
      });
  }, [isLoading, team.inviteWaiting, searchUserInviteWaiting, sortedInviteUser]);

  const filteredUsers = useMemo(() => {
    if (isLoading) return [];
    const list = Array.isArray(team.inviteUser) ? team.inviteUser : [];

    // L'owner ne doit pas apparaître dans sa propre liste d'équipe
    const ownerUserId = ownerProfile?.ID ?? ownerId;

    return list
      .filter((user) => {
        if (ownerUserId && String(user.ID) === String(ownerUserId)) return false;
        if (superAdmin) return true;
        if (adminProfile && team.displayRoleByUser(user.ID) === "Admin") return false;

        const adminName = (adminProfile?.display_name || "").toLowerCase();
        const userName = (user?.display_name || "").toLowerCase();
        return adminName ? userName !== adminName : true;
      })
      .filter((user) => {
        const q = (searchUser || "").toLowerCase();
        const fields = [user.display_name, user.first_name, user.last_name, user.user_login]
          .filter(Boolean)
          .map((s) => s.toLowerCase());
        return fields.some((f) => f.includes(q));
      })
      .sort((a, b) => {
        let valA, valB;
        switch (sorted) {
          case "ascLogin":
            valA = a.user_login || "";
            valB = b.user_login || "";
            return valA.localeCompare(valB);
          case "dscLogin":
            valA = a.user_login || "";
            valB = b.user_login || "";
            return valB.localeCompare(valA);
          case "ascName":
            valA = a.last_name || "";
            valB = b.last_name || "";
            return valA.localeCompare(valB);
          case "dscName":
            valA = a.last_name || "";
            valB = b.last_name || "";
            return valB.localeCompare(valA);
          default:
            valA = a.display_name || "";
            valB = b.display_name || "";
            return valA.localeCompare(valB);
        }
      });
  }, [isLoading, team.inviteUser, searchUser, sorted, superAdmin, adminProfile, team.displayRoleByUser, ownerProfile, ownerId]);

  const getTimeReading = (unix) =>
    new Date(unix * 1000).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" });

  const getTimeExpired = (unix) => {
    const d = new Date(unix * 1000);
    d.setDate(d.getDate() + 15);
    return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" });
  };

  return (
    <div className={styles.contentArea}>
      {isLoading ? (
        <Loader />
      ) : (
        <>
          {/* INVITATIONS SECTION */}
          <div className={styles.drawerSection}>
            <button
              type="button"
              className={styles.collapsibleHeader}
              onClick={() => setInvitationsOpen((prev) => !prev)}
            >
              <span className={styles.drawerSectionTitle}>{t("team.my_pending_invitations")}</span>
              {invitationsOpen ? <FiChevronUp /> : <FiChevronDown />}
            </button>
            {invitationsOpen && (
              <div className={styles.collapsibleContent}>
                <div className={styles.wrapperUser}>
                  <div className={styles.searchUser}>
                    <InputForms
                      id={styles.inputSearchInvitation}
                      onChange={(e) => setSearchUserInviteWaiting(e.target.value)}
                      placeholder={t("team.search_invitation")}
                    />
                  </div>

                  {filteredInvites.length === 0 ? (
                    <div style={{ opacity: 0.7, fontWeight: 800 }}>{t("team.no_pending_invitation")}</div>
                  ) : (
                    filteredInvites.map((invit, index) => (
                      <ResendInviteCard
                        key={index}
                        dateInvitation={getTimeReading(invit.when)}
                        dateExpiration={getTimeExpired(invit.when)}
                        inviteWaiting={team.inviteWaiting}
                        setinviteWaiting={team.setInviteWaiting}
                        invit={invit}
                        hashId={invit.hash}
                        inviteEmail={invit.email}
                        inviteName={invit.display_name}
                        newInvite={team.newInvite}
                        setNewInvite={team.setNewInvite}
                      />
                    ))
                  )}
                </div>
              </div>
            )}
          </div>

          {/* TEAM SECTION */}
          <div className={styles.drawerSection}>
            <button
              type="button"
              className={styles.collapsibleHeader}
              onClick={() => setTeamOpen((prev) => !prev)}
            >
              <span className={styles.drawerSectionTitle}>{t("team.my_team")}</span>
              {teamOpen ? <FiChevronUp /> : <FiChevronDown />}
            </button>
            {teamOpen && (
              <div className={styles.collapsibleContent}>
                <div className={styles.wrapperUser}>
                  <div className={styles.wrapperInviteAndInput}>
                {!showInvite ? (
                  <>
                    <div className={styles.searchUser}>
                      <InputForms
                        id={styles.inputSearchAudio}
                        onChange={(e) => setSearchUser(e.target.value)}
                        placeholder={t("team.search_users")}
                      />
                    </div>

                    <button
                      type="button"
                      className={styles.btnPrimary}
                      onClick={toggleInvite}
                    >
                      <BiMailSend style={{ marginRight: 6 }} /> {t("team.invite_user")}
                    </button>
                  </>
                ) : (
                  <div className={styles.inviteCardWrapper}>
                    <button className={styles.btnDanger} onClick={toggleInvite} type="button">
                      {t("team.close_invitation")}
                    </button>

                    <div className={styles.inviteCard}>
                      <InviteForms
                        setReloadWaitingInvite={team.setReloadWaitingInvite}
                        reloadWaitingInvite={team.reloadWaitingInvite}
                        toggle={() => setShowInvite(false)} // ✅ ferme après envoi si tu l’appelles
                      />
                    </div>
                  </div>
                )}
                  </div>

                  <div className={styles.searchAndCard}>
                <UserCard
                  inviteUser={filteredUsers}
                  cabines={allCabines}
                  setSorted={setSorted}
                  displayRoleByUser={team.displayRoleByUser}
                  selectDefaultValue={team.selectDefaultValue}
                  setUpdateRole={team.setUpdateRole}
                  setUpateUserId={team.setUpdateUserId}
                  inviteId={inviteId}
                  userAuth={userAuth}
                  setCabId={team.setCabId}
                  setMaj={team.setMaj}
                  maj={team.maj}
                  majRole={team.majRole}
                  setMajRole={team.setMajRole}
                  createPermissionCabByCab={team.createPermissionCabByCab}
                  numberCabsAuth={team.numberCabsAuth}
                />
              </div>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}